import { z } from 'zod';
import { pollerConfig } from './poller-config';

/**
 * BOUNDED history of email-poller ticks (v1.13.5, §17.1). The email poller is a single
 * platform-level loop; this read-only surface lets an OPERATOR confirm it runs + behaves
 * as configured (cadence, active hours, demand-driven idleness). Like the poller config it
 * is a GLOBAL/OPERATOR resource — the API (`GET /api/admin/poller-runs`) is gated to the
 * platform-operator allow-list, never reachable per workspace.
 *
 * `pollerRun` is one persisted tick; `pollerRunsResponse` wraps the newest-first list plus
 * a `summary` (the current config + last-run instant) so the UI can render a headline
 * without a second request. Counts/timings come from the tick's observability summary; no
 * PII (I6) crosses this boundary.
 */

/** Default page size for the runs list. */
export const POLLER_RUNS_DEFAULT_LIMIT = 20;
/** Hard cap on the runs list page size. */
export const POLLER_RUNS_MAX_LIMIT = 100;

/**
 * Classified tick outcome:
 *  - `polled`         — the tick had due targets and polled at least one connection.
 *  - `idle_no_agents` — active window, but no due poll targets (no workspace waiting on mail).
 *  - `idle_off_hours` — outside the operator's active-hours window; a zero-query no-op.
 *  - `error`          — the tick hit an error (target resolution or a per-connection poll).
 */
export const pollerRunOutcome = z.enum(['polled', 'idle_no_agents', 'idle_off_hours', 'error']);
export type PollerRunOutcome = z.infer<typeof pollerRunOutcome>;

/**
 * Filtered-reason breakdown for one poller tick (#306). Explains WHY ingested mail did NOT
 * wake an agent, so an operator diagnosing "my inbox agent gets no messages" can see whether
 * mail was suppressed and by which rule. Every field counts per applicable trigger (mirroring
 * the dispatcher's own suppression counters) except `noMatchingTrigger` and `duplicates`,
 * which are per event. Nullable on `pollerRun` — rows written before #306 carry no breakdown.
 *
 * Buckets:
 *  - `selfSuppressed`     — #73 self/sent or non-Inbox suppression (the connected account's own mail).
 *  - the six classifier reasons (§17.1 `classifyNonActionable`): obvious no-reply mail.
 *  - `noMatchingTrigger`  — ingested but NO enabled agent trigger subscribes to it (no run possible).
 *  - `duplicates`         — a redelivered event whose run already existed (no second run).
 */
export const pollerRunFilterBreakdown = z.object({
  /** #73 self/sent (or non-actionable-to-self) trigger suppressions. */
  selfSuppressed: z.number().int().nonnegative(),
  /** Automated / no-reply / DSN-daemon sender (§17.1 `noreply-sender`). */
  noreplySender: z.number().int().nonnegative(),
  /** Automated calendar-notification sender (§17.1 `calendar-auto`). */
  calendarAuto: z.number().int().nonnegative(),
  /** Bulk/list mail — `List-Unsubscribe`/`List-Id` (§17.1 `list-mail`). */
  listMail: z.number().int().nonnegative(),
  /** RFC 3834 auto-generated (§17.1 `auto-submitted`). */
  autoSubmitted: z.number().int().nonnegative(),
  /** `Precedence: bulk|list|auto_reply` (§17.1 `bulk-precedence`). */
  bulkPrecedence: z.number().int().nonnegative(),
  /** Delivery/bounce report (§17.1 `delivery-report`). */
  deliveryReport: z.number().int().nonnegative(),
  /** Ingested but no enabled agent trigger subscribes to this event (`applicable.length===0`). */
  noMatchingTrigger: z.number().int().nonnegative(),
  /** Redelivered event whose run already existed — no second run created. */
  duplicates: z.number().int().nonnegative(),
});
export type PollerRunFilterBreakdown = z.infer<typeof pollerRunFilterBreakdown>;

/** One persisted poller tick (newest-first in the list). */
export const pollerRun = z.object({
  id: z.string(),
  /** ISO-8601 UTC instant the tick began. */
  startedAt: z.string().datetime(),
  /** Wall-clock duration of the tick, milliseconds. */
  durationMs: z.number().int().nonnegative(),
  outcome: pollerRunOutcome,
  /** Due poll targets resolved this tick (before per-connection backoff gating). */
  targets: z.number().int().nonnegative(),
  /** Connections actually polled (targets not skipped by backoff). */
  connectionsPolled: z.number().int().nonnegative(),
  /** Newly-stored (post-dedupe) emails ingested. */
  emailsIngested: z.number().int().nonnegative(),
  /** Runs spawned from the ingested emails. */
  runsSpawned: z.number().int().nonnegative(),
  /** Emails suppressed by the non-actionable auto-skip filter (§12.2). */
  nonActionableSkipped: z.number().int().nonnegative(),
  /**
   * #306 filtered-reason breakdown (self/sent + classifier reasons + no-matching-agent +
   * duplicate). Null for ticks recorded before #306. `filtered` in the UI = the sum of
   * these buckets.
   */
  filterBreakdown: pollerRunFilterBreakdown.nullable(),
  /** External connector (Gmail) API calls issued. */
  apiCalls: z.number().int().nonnegative(),
  /** Named DB queries issued (0 for an idle/off-hours no-op tick). */
  dbQueries: z.number().int().nonnegative(),
  /** False when the tick hit an error. */
  ok: z.boolean(),
  /** Error summary when `ok=false`; null otherwise. */
  error: z.string().nullable(),
});
export type PollerRun = z.infer<typeof pollerRun>;

/** Headline context for the UI: the current config + the last-run instant. */
export const pollerRunsSummary = z.object({
  config: pollerConfig,
  /** ISO-8601 UTC instant of the most recent tick, or null when none recorded yet. */
  lastRunAt: z.string().datetime().nullable(),
});
export type PollerRunsSummary = z.infer<typeof pollerRunsSummary>;

/** The `GET /api/admin/poller-runs` response: newest-first runs + a summary headline. */
export const pollerRunsResponse = z.object({
  runs: z.array(pollerRun),
  summary: pollerRunsSummary,
});
export type PollerRunsResponse = z.infer<typeof pollerRunsResponse>;
